<template>
  <component v-if="notFound" :is="notFoundComponent"></component>
  <section v-else class="page-container" :class="{ 'collapsed-mode': collapsedCardsModeOn }">

    <!-- PAGE LOADER -->
    <div class="loader-container" v-if="!loaded">
      <custom-spinner></custom-spinner>
    </div>

    <!-- PAGE ERROR -->
    <div class="container mt-4" v-else-if="error">
      <div class="alert alert-danger">{{ error | translate('msg') }}</div>
    </div>

    <div class="container-fluid cards-container py-4 px-xl-5" v-if="caseSummary">

      <!-- CASE DETAILS -->
      <policy-case-details :headerId="caseHeaderId" :batchId="caseSummary.batchID" :version="caseSummary.version"
        :caseType="caseSummary.productLineName" :status="caseSummary.status" :statusReason="caseSummary.statusReason"
        :active="caseActive" :userLocked="caseSummary.userLocked" :isLockedForMe="isLockedForMe"
        :hasNoEditRights="hasNoEditRights" :lastUser="caseSummary.lastUser" :lastUpdated="caseSummary.lastUpdated"
        :customerUniqueId="caseSummary.customerUniqueId" :customerName="caseSummary.customerName"
        @reload-case="loadCase" @error="setError"></policy-case-details>

      <div class="row mt-4"
        :class="{ 'policy-summaries-loaded': (responseSummariesAreLoaded || !responsesExists) && isRequestedSummaryLoaded }"
        id="policyCase-summaries-loader">
        <div class="col col-policy-summaries" id="policyCase-col-policy-summaries">
          <div class="row" id="policyCase-summaries-row">
            <div class="col col-request-summary" v-if="requestedPolicyId" id="policyCase-summaries-col">

              <div class="col-label" id="policyCase-policy-col-label"><span>{{ 'REQUESTED_SUMMARY_LABEL' | translate
              }}</span></div>

              <!-- PREVIEW REQUESTED SUMMARY -->
              <template v-if="previewModeOn">
                <policy-card :policySummary="previewRequestedSummary" :collapsed="collapsedCardsModeOn"
                  :originalPremium="previewOriginalPremiums['requestedSummary']" class="preview"
                  @show-validations="showValidationsModal" id="policyCase-policyCard"></policy-card>
              </template>

              <!-- REQUESTED SUMMARY -->
              <template v-else>
                <policy-card ref="requestedSummary" :policyId="requestedPolicyId"
                  :productLine="caseSummary.productLineName" :collapsed="collapsedCardsModeOn"
                  :class="{ 'clicked': clickedPolicyId === requestedPolicyId }"
                  @click.native="summaryCardOnClick(requestedPolicyId)" @show-validations="showValidationsModal"
                  @val-exclude-audit-updated="onValOtherThanAuditUpdate" @val-error-updated="onValErrorsUpdate"
                  @summary-loaded="isRequestedSummaryLoaded = true" id="policyCase-requestedSummary"></policy-card>
              </template>

            </div>

            <div class="col col-response-summaries" v-if="responsesExists && !resettingCarouselRefs"
              id="policyCase-response-summaries">

              <div class="col-label" id="policyCase-requestSummary-col-label"><span>{{ 'RESPONSE_SUMMARIES_LABEL' |
                translate
              }}</span></div>

              <!-- CAROUSEL -->
              <VueSlickCarousel ref="responsesCarousel" v-bind="carouselSettings" :class="[{
                'prev-highlighted': this.prevArrowHighlighted,
                'next-highlighted': this.nextArrowHighlighted
              },
              this.prevArrowValidationClass,
              this.nextArrowValidationClass]" id="policyCase-vueSlickCarousel" @afterChange="onCarouselPageChange">

                <!-- PREVIEW RESPONSE SUMMARIES -->
                <template v-if="previewModeOn">
                  <div v-for="summary in previewResponseSummaries" :key="'previewContainer_' + summary.productName"
                    class="card-holder">
                    <policy-card :ref="'preview_' + summary.productName" :key="'preview_' + summary.productName"
                      :policySummary="summary" :collapsed="collapsedCardsModeOn"
                      :originalPremium="previewOriginalPremiums[summary.productName]" class="preview"
                      @show-validations="showValidationsModal" @summary-loaded="onPreviewSummaryLoaded"
                      :id="'policyCase' + summary.productName"></policy-card>
                  </div>
                </template>

                <!-- RESPONSE SUMMARIES -->
                <template v-else>
                  <div v-for="policyId in responsePolicyIds" :key="'responseSummaryContainer_' + policyId"
                    class="card-holder">
                    <policy-card :ref="'responseSummary_' + policyId" :key="'responseSummary_' + policyId"
                      :policyId="policyId" :collapsed="collapsedCardsModeOn" :class="{
                        'clicked': clickedPolicyId === policyId,
                        'selected': selectedPolicyId === policyId,
                        'selection-mode': modifySelectedPolicyModeOn
                      }" @click.native="summaryCardOnClick(policyId)" @show-validations="showValidationsModal"
                      @val-exclude-audit-updated="onValOtherThanAuditUpdate" @val-error-updated="onValErrorsUpdate"
                      @summary-loaded="onSummaryLoaded" :id="'policyCase' + policyId"></policy-card>
                  </div>
                </template>

              </VueSlickCarousel>

            </div>
          </div>
        </div>
        <div class="col col-buttons-component" id="policyCase-buttons-component">

          <!-- ACTIONS -->
          <policy-case-buttons :status="caseSummary.status" :active="caseActive" :isLockedForMe="isLockedForMe"
            :hasNoEditRights="hasNoEditRights" :errorsOnRequestedPolicy="errorsOnRequestedPolicy"
            :validationsOnSelectedPolicy="validationsOnSelectedPolicy" :collapsed="collapsedCardsModeOn"
            :modifySelected="modifySelectedPolicyModeOn" :formRendered="formRendered" :preview="previewModeOn"
            @switch-collapsed-mode="collapsedCardsModeOn = !collapsedCardsModeOn" @case-recalculate="onRecalculate"
            @case-preview="onPreview" @download-vpi="onDownloadVpi" @case-close="closeCaseModalShown = true"
            @case-reopen="reopenCase" @selected-modify="onSelectedModify" @case-accept="acceptCase"
            @case-show-history="showHistoryOverview" @download-case="downloadCase" @save-case="saveCase"
            @case-accept-clicked="onAcceptClicked" @accepted-manually-clicked="onAcceptManuallyClicked"
            @accepted-manually="acceptManuallyCaseModalShown = true"></policy-case-buttons>

        </div>
      </div>
    </div>

    <!-- FORM -->
    <div class="container-fluid form-container mt-4" v-if="clickedPolicyId && showForm">
      <policy-form ref="policyFormChild" :policyId="clickedPolicyId" :formConfigType="formConfigType"
        :readOnly="formReadOnly" :anchorsOffset="341" @form-submit="onFormSubmit" @form-invalid="loaded = true"
        @form-rendered="onFormRendered"></policy-form>
    </div>

    <!-- COLLAPSED MODE FORM OVERLAY -->
    <div v-if="!collapsedCardsModeOn" class="form-container-overlay"></div>

    <!-- MODAL HISTORY -->
    <vodal :show="historyOverviewShown" :closeOnEsc="true" :closeOnClickMask="false" :width="800"
      :customStyles="{ 'top': '80px', 'bottom': 'auto', 'height': '80%' }" @hide="hideHistoryOverview">
      <history-overview :policycaseHeaderId="caseHeaderId" :historyPolicycases="historyPolicycases"
        :loading="historyPolicycasesLoading" :error="historyPolicycasesError"
        @hide-history-overview="hideHistoryOverview"></history-overview>
    </vodal>

    <!-- MODAL CLOSE CASE -->
    <vodal :show="closeCaseModalShown" :closeOnEsc="true" :closeOnClickMask="false" :width="600"
      :customStyles="{ 'top': '80px', 'bottom': 'auto', 'height': '80%', 'max-height': '530px' }"
      @hide="closeCaseModalShown = false">
      <policy-case-close @execute-case-close="closeCase"></policy-case-close>
    </vodal>

    <vodal :show="acceptManuallyCaseModalShown" :closeOnEsc="true" :closeOnClickMask="false" :width="600"
      :customStyles="{ 'top': '80px', 'bottom': 'auto', 'height': '80%', 'max-height': '530px' }"
      @hide="acceptManuallyCaseModalShown = false">
      <policy-case-accept-manually @execute-case-accept-manually="acceptedManually"></policy-case-accept-manually>
    </vodal>

    <!-- MODAL VALIDATIONS -->
    <vodal :show="validationsModalShown" :closeOnEsc="true" :closeOnClickMask="false" :width="600"
      :customStyles="{ 'top': '80px', 'bottom': 'auto', 'height': 'auto', 'max-height': '90%' }"
      @hide="validationsModalShown = false">
      <policy-validations :product="validationsModalObj.product"
        :validations="validationsModalObj.validations || []"></policy-validations>
    </vodal>
    <vodal :show="showRecalcConfirm" :closeOnEsc="true" :closeOnClickMask="false" :width="600"
      :customStyles="{ 'top': '80px', 'bottom': 'auto', 'height': '80%', 'max-height': '250px' }"
      @hide="showRecalcConfirm = false">
      <policy-case-recalculate @yes="onRecalcConfirmYes" @no="showRecalcConfirm = false" />
    </vodal>
  </section>
</template>

<script>
import PolicyCaseDetails from './PolicyCaseDetails';
import PolicyCaseButtons from './PolicyCaseButtons';
import PolicyCard from './PolicyCard';
import PolicyValidations from './PolicyValidations';
import PolicyForm from './PolicyForm';
import PolicyCaseClose from './PolicyCaseClose';
import PolicyCaseAcceptManually from './PolicyCaseAcceptManually';
import PolicyCaseRecalculate from './PolicyCaseRecalculate';
import CustomSpinner from '../CustomSpinner';
import HistoryOverview from '../HistoryOverview';
import NotFound from '../NotFound';
import RestService from '../../service/RestService';
import GeneralConfigService from '../../service/GeneralConfigService';
import FormConfigService from '../../service/FormConfigService';
import KeycloakService from '../../service/keycloak/KeycloakService';
import { Roles } from '../../service/keycloak/Roles';
import Vodal from 'vodal';
import VueSlickCarousel from 'vue-slick-carousel';
import 'vue-slick-carousel/dist/vue-slick-carousel.css';

export default {
  props: ['caseHeaderId', 'historicalPolicycaseId'],
  components: {
    PolicyCaseDetails,
    PolicyCaseButtons,
    PolicyCard,
    PolicyValidations,
    PolicyCaseClose,
    PolicyCaseAcceptManually,
    PolicyCaseRecalculate,
    CustomSpinner,
    HistoryOverview,
    PolicyForm,
    Vodal,
    VueSlickCarousel

  },
  data: function () {
    return {
      rest: this.initRestService(),
      batchId: null,
      caseSummary: null,
      caseActive: null,
      canEditCase: null,
      notFoundComponent: NotFound,
      loaded: false,
      showForm: false,
      loadedSummaries: [],
      loadedPreviewSummaries: [],
      summariesValIconClasses: [],
      previewsValIconClasses: [],
      requestedPolicyId: null,
      responsePolicyIds: [],
      selectedPolicyId: null,
      clickedPolicyId: null,
      modifySelectedOriginalId: null,
      modifySelectedPolicyModeOn: false,
      collapsedCardsModeOn: true,
      prevArrowHighlighted: false,
      nextArrowHighlighted: false,
      prevArrowValidationClass: 'prev-none',
      nextArrowValidationClass: 'next-none',
      notFound: false,
      error: null,
      formSubmitFlag: null,
      previewModeOn: false,
      previewRequestedSummary: {},
      previewResponseSummaries: [],
      previewOriginalPremiums: {},
      roles: Roles,
      isLockedForMe: true,
      historyPolicycases: [],
      historyPolicycasesLoading: false,
      historyPolicycasesError: null,
      historyOverviewShown: false,
      closeCaseModalShown: false,
      acceptManuallyCaseModalShown: false,
      validationsModalObj: {},
      validationsModalShown: false,
      validationsOnSelectedPolicy: true,
      errorsOnRequestedPolicy: true,
      formRendered: false,
      resettingCarouselRefs: false,
      carouselSettings: {
        arrows: true,
        draggable: false,
        infinite: false,
        slidesToShow: 3,
        slidesToScroll: 1,
        speed: 800,
        responsive: [
          { breakpoint: 1600, settings: { slidesToShow: 2 } },
          { breakpoint: 1300, settings: { slidesToShow: 1 } }
        ]
      },
      isRequestedSummaryLoaded: false,
      responseSummariesAreLoaded: false,
      showRecalcConfirm: false
    }
  },
  async beforeMount() {
    await this.loadCase();
    this.showForm = true;
  },
  computed: {
    hasNoEditRights() {
      return !(this.isRoleBasedAccessAllowed([this.roles.ADMIN]) ||
        (this.isRoleBasedAccessAllowed([this.roles.USER])))
    },
    responsesExists() {
      return (!this.previewModeOn && this.responsePolicyIds.length > 0) ||
        (this.previewModeOn && this.previewResponseSummaries.length > 0);
    },
    formReadOnly() {
      return !this.caseActive ||
        (['ACCEPTED', 'CLOSED'].includes(this.caseSummary.status)) ||
        (this.clickedPolicyId !== this.requestedPolicyId);
    },
    formConfigType() {
      return this.caseSummary.productLineName + FormConfigService.typeEditPolicySuffix;
    }
  },
  methods: {
    initRestService() {
      return new RestService();
    },
    async loadCase() {
      this.loaded = false;
      await this.updatePolicyCaseDetails();
      this.loaded = true;
      this.setLockedForMe();
    },
    async updatePolicyCaseDetails() {
      this.setError(null);
      this.caseActive = !this.historicalPolicycaseId;
      try {
        let response = this.caseActive
          ? await this.rest.getPolicyCaseSummary(this.caseHeaderId)
          : await this.rest.getHistoricalPolicyCaseSummary(this.historicalPolicycaseId);
        this.canEditCase = response.headers['x-auth-can-edit'] === 'true';
        this.caseSummary = response.data;
        this.batchId = this.caseSummary.batchId;
        this.requestedPolicyId = this.caseSummary.requestedPolicyId;
        this.clickedPolicyId = this.caseSummary.requestedPolicyId;
        this.responsePolicyIds = this.caseSummary.responsePolicyIds;
        this.selectedPolicyId = this.caseSummary.selectedPolicyId;
        this.loadedSummaries = [];
      } catch (error) {
        this.loaded = true;
        if (error.response && error.response.status && (error.response.status === '404' || error.response.status === 404)) {
          this.notFound = true;
          this.$log.error('404 Not Found');
        } else {
          this.setError('ERROR_LOADING_DETAILS');
          this.$log.error(error.response.data);
        }
      }
    },
    async setLockedForMe() {
      if (this.caseSummary.userLocked && !KeycloakService.hasRole('admin')) {
        let myUserName = await GeneralConfigService.getMyUserName();
        if (this.caseSummary.lastUser !== myUserName) {
          this.isLockedForMe = true;
          return;
        }
      }
      this.isLockedForMe = false;
    },
    summaryCardOnClick(policyId) {
      this.clickedPolicyId = policyId;
      if (this.modifySelectedPolicyModeOn) this.selectedPolicyId = policyId;
    },
    async changeCaseStatus(status, statusReasonAndDate) {
      this.setError(null);
      try {
        await this.rest.updateCaseStaus(this.caseHeaderId, status, statusReasonAndDate || null);
        this.loadCase();
      } catch (error) {
        this.setError('ERROR_DURING_OPERATION');
        this.$log.error(error.response.data);
        this.loaded = true;
      }
    },
    onRecalculate() {
      this.loaded = false;
      this.loadedSummaries = [];
      this.formSubmitFlag = 'recalc';
      this.isRequestedSummaryLoaded = false;
      this.responseSummariesAreLoaded = false;
      this.summariesValIconClasses = [];
      this.triggerFormSubmitOnPolicyFormChild();
    },
    onPreview() {
      if (this.previewModeOn) {
        this.previewModeOn = false;
        this.previewResponseSummaries = [];
        this.previewRequestedSummary = {};
        this.loadedPreviewSummaries = [];
      } else {
        this.loaded = false;
        this.loadedSummaries = [];
        this.formSubmitFlag = 'preview';
        this.triggerFormSubmitOnPolicyFormChild();
      }
    },
    onDownloadVpi() {
      let fileContent = 'Case id: ' + this.caseSummary.policycaseHeaderId + '\n';
      for (let index in this.previewResponseSummaries) {
        fileContent += 'Product: ' + this.previewResponseSummaries[index].product;
        fileContent += '\nRequest: \n' + this.previewResponseSummaries[index].vpiRequest;
        fileContent += '\nResponse: \n' + this.previewResponseSummaries[index].vpiResponse + '\n\n';
      }
      this.triggerDownload('vpiMessages-caseId-' + this.caseSummary.policycaseHeaderId + '.txt', fileContent);
    },
    triggerDownload(filename, text) {
      const url = window.URL.createObjectURL(new Blob([text]));
      const link = this.createDomElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.click();
      window.URL.revokeObjectURL(url);
    },
    createDomElement(tagName) {
      return document.createElement(tagName);
    },
    triggerFormSubmitOnPolicyFormChild() {
      if (this.clickedPolicyId === this.requestedPolicyId) {
        this.$refs.policyFormChild.submit();
      } else {
        this.clickedPolicyId = this.requestedPolicyId;
        // here we are counting on the child component watcher to trigger the submit
        this.$refs.policyFormChild.setSubmitFlag(true);
      }
    },
    onFormRendered() {
      this.formRendered = true;
      this.loaded = true;
    },
    onFormSubmit(submissionData) {
      if (this.formSubmitFlag === 'recalc') {
        this.recalculate(submissionData);
      } else if (this.formSubmitFlag === 'preview') {
        this.recalculatePreview(submissionData);
      } else if (this.formSubmitFlag === 'accept') {
        this.acceptCaseOnSubmit();
      }
      this.formSubmitFlag = null;
    },
    acceptCaseOnSubmit() {
      this.changeCaseStatus('ACCEPTED');
    },
    async recalculatePreview(submissionData) {
      this.setError(null);
      try {
        this.modifySubmitionDateAsPerNewCoverageForSUIV(submissionData);
        let response = await this.rest.previewPolicyCase(submissionData, this.caseHeaderId);
        this.previewRequestedSummary = response.data.requestedPolicySummary;
        this.previewResponseSummaries = response.data.responsePoliciesSummary;
        this.setOriginalPremiums();
        this.previewModeOn = true;
        this.loaded = true;
      } catch (error) {
        this.setError('ERROR_DURING_OPERATION');
        this.$log.error(error.response.data);
        this.loaded = true;
      }
    },
    async recalculate(submissionData) {
      this.setError(null);
      this.selectedPolicy = null;
      this.modifySubmitionDateAsPerNewCoverageForSUIV(submissionData);
      try {
        await this.rest.recalculatePolicyCase(submissionData, this.caseHeaderId);
        this.loadCase();
      } catch (error) {
        this.setError('ERROR_DURING_OPERATION');
        this.$log.error(error.response.data);
        this.loaded = true;
      }
    },
    setOriginalPremiums() {
      let components = Object.keys(this.$refs).filter(refKey => refKey.startsWith('responseSummary_'));
      components.unshift('requestedSummary');
      components.forEach(component => {
        if (typeof this.$refs[component] === 'undefined') return;
        let result = this.$refs[component].getProductPremiumInfo();
        this.previewOriginalPremiums[result.product] = result.premium;
      });
    },
    async onSelectedModify() {
      if (this.modifySelectedPolicyModeOn) {
        if (this.modifySelectedOriginalId !== this.selectedPolicyId) {
          try {
            await this.rest.setSelectedPolicyOfPolicycase(this.caseHeaderId, this.selectedPolicyId);
          } catch (error) {
            this.setError('ERROR_DURING_OPERATION');
            this.$log.error(error.response.data);
          }
        }
      } else {
        this.modifySelectedOriginalId = this.selectedPolicyId;
      }
      this.modifySelectedPolicyModeOn = !this.modifySelectedPolicyModeOn;
    },
    closeCase(statusReasonAndDate) {
      this.closeCaseModalShown = false;
      this.previewModeOn = false;
      this.loaded = false;
      this.changeCaseStatus('CLOSED', statusReasonAndDate);
    },
    acceptCase() {
      this.loaded = false;
      this.modifySelectedPolicyModeOn = false;
      this.formSubmitFlag = 'accept';
      this.triggerFormSubmitOnPolicyFormChild();
    },
    acceptedManually(statusReasonAndDate) {
      this.acceptManuallyCaseModalShown = false;
      this.loaded = false;
      this.modifySelectedPolicyModeOn = false;
      this.changeCaseStatus('ACCEPTED_MANUALLY', statusReasonAndDate);
    },
    reopenCase() {
      this.previewModeOn = false;
      this.loaded = false;
      this.changeCaseStatus('REOPENED');
    },
    isRoleBasedAccessAllowed(roles) {
      return KeycloakService.isRoleBasedAccessAllowed(roles);
    },
    async loadHistoryPolicycases() {
      this.historyPolicycasesLoading = true;
      this.historyPolicycasesError = null;
      try {
        let res = await this.rest.getHistoryPolicycases(this.caseHeaderId);
        this.historyPolicycases = res.data;
      } catch (error) {
        this.historyPolicycasesError = 'SOMETHING_WRONG_FETCH_HISTORY_POLICYCASES';
        this.$log.error(error.response.data);
      }
      this.historyPolicycasesLoading = false;
    },
    showHistoryOverview() {
      this.historyOverviewShown = true;
      this.loadHistoryPolicycases();
    },
    hideHistoryOverview() {
      this.historyOverviewShown = false;
      this.historyPolicycasesLoading = false;
      this.historyPolicycasesError = null;
    },
    showValidationsModal(validationsObj) {
      this.validationsModalObj = validationsObj;
      this.validationsModalShown = true;
    },
    onCarouselPageChange(slideIndex) {
      this.setCarouselArrowColor();
      this.setCarouselArrowIndication();
    },
    setCarouselArrowColor() {
      let responsesCarousel = document.querySelector('.slick-slider');
      let selectedResponseCard = responsesCarousel.querySelector('.card.selected');
      if (selectedResponseCard) {
        let firstActiveSlideIndex = responsesCarousel.querySelector('.slick-slide.slick-active').getAttribute('data-index');
        let selectedResponseCardParentSlide = selectedResponseCard.closest('.slick-slide');
        if (!selectedResponseCardParentSlide.classList.contains('slick-active')) {
          if (firstActiveSlideIndex > selectedResponseCardParentSlide.getAttribute('data-index')) {
            this.prevArrowHighlighted = true;
            this.nextArrowHighlighted = false;
          } else if (firstActiveSlideIndex < selectedResponseCardParentSlide.getAttribute('data-index')) {
            this.nextArrowHighlighted = true;
            this.prevArrowHighlighted = false;
          }
        } else {
          this.prevArrowHighlighted = false;
          this.nextArrowHighlighted = false;
        }
      }
    },
    setCarouselArrowIndication() {
      let responsesCarousel = document.querySelector('.slick-slider');
      let firstActiveSlideIndex = responsesCarousel.querySelector('.slick-slide.slick-active').getAttribute('data-index');
      let iconClasses = this.previewModeOn ? this.previewsValIconClasses : this.summariesValIconClasses;
      let currentBreakpoint = this.carouselSettings.responsive.slice().reverse().find(bp => bp.breakpoint >= window.innerWidth);
      if (!currentBreakpoint) currentBreakpoint = { settings: { slidesToShow: this.carouselSettings.slidesToShow } };
      let iconClassesBeforeActiveSlides = iconClasses.slice(0, firstActiveSlideIndex);
      let iconClassesAfterActiveSlides = iconClasses.slice(parseInt(firstActiveSlideIndex) + parseInt(currentBreakpoint.settings.slidesToShow));
      this.prevArrowValidationClass = this.chooseArrowValidationClass(iconClassesBeforeActiveSlides, 'prev-border-');
      this.nextArrowValidationClass = this.chooseArrowValidationClass(iconClassesAfterActiveSlides, 'next-border-');
    },
    chooseArrowValidationClass(iconClasses, prefix) {
      if (iconClasses.includes('bg-danger')) return prefix + 'danger';
      else if (iconClasses.includes('bg-warning')) return prefix + 'warning';
      else if (iconClasses.includes('bg-info')) return prefix + 'info';
      else if (iconClasses.includes('bg-light')) return prefix + 'light';
      else return '';
    },
    setError(errorMessage) {
      this.error = errorMessage;
    },
    onValOtherThanAuditUpdate(policyId, validationsOtherThanAuditExist) {
      if (policyId === this.selectedPolicyId) this.validationsOnSelectedPolicy = validationsOtherThanAuditExist;
    },
    onValErrorsUpdate(policyId, errorsExist) {
      if (policyId === this.requestedPolicyId) this.errorsOnRequestedPolicy = errorsExist;
    },
    onSummaryLoaded(policyId) {
      this.loadedSummaries.push(policyId);
      if (this.loadedSummaries.length >= this.responsePolicyIds.length) {
        /* It's important that we use this.responsePolicyIds for the loop below and not this.loadedSummaries
        because the latter can have order different that what is visually in the carousel */
        this.responsePolicyIds.forEach(responseId => this.summariesValIconClasses.push(this.$refs['responseSummary_' + responseId].validationsIconClass));
        this.setCarouselArrowColor();
        this.setCarouselArrowIndication();
        this.responseSummariesAreLoaded = true;
      }
    },
    onPreviewSummaryLoaded(summaryProduct) {
      this.loadedPreviewSummaries.push(summaryProduct);
      if (this.loadedPreviewSummaries.length >= this.previewResponseSummaries.length) {
        this.previewResponseSummaries.forEach(previewSummary => {
          const previewRef = this.$refs['preview_' + previewSummary.product];
          if (previewRef && previewRef.validationsIconClass) {
            this.previewsValIconClasses.push(previewRef.validationsIconClass);
          } else {
            console.error(`Reference or validationsIconClass not found for product: ${previewSummary.product}`);
          }
        });
        this.setCarouselArrowIndication();
      }
    },
    async onAcceptClicked() {
      const formRef = this.$refs.policyFormChild;
      const formChanged = formRef && typeof formRef.hasDataChanged === 'function'
        ? await formRef.hasDataChanged()
        : false;
      if (formChanged) {
        this.showRecalcConfirm = true;
      } else {
        this.acceptCase();
      }
    },
    async onAcceptManuallyClicked() {
      const formRef = this.$refs.policyFormChild;
      const formChanged = formRef && typeof formRef.hasDataChanged === 'function'
        ? await formRef.hasDataChanged()
        : false;
      if (formChanged) {
        this.showRecalcConfirm = true;
      } else {
        this.acceptManuallyCaseModalShown = true;
      }
    },
    onRecalcConfirmYes() {
      this.showRecalcConfirm = false;
      this.onRecalculate();
    },
    async downloadCase() {
      try {
        await this.rest.downloadCase(this.caseHeaderId).then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
          const link = this.createDomElement('a');
          link.href = url;
          link.setAttribute('download', this.caseHeaderId + '.pdf');
          link.click();
          window.URL.revokeObjectURL(url);
        });
      } catch (error) {
        this.setError('ERROR_DURING_OPERATION');
        this.$log.error(error.response.data);
      }
    },
    async saveCase() {
      try {
        await this.rest.saveCase(this.caseHeaderId).then((response) => {
          this.closeCase('Policy accepted and stored in backoffice');
        });
      } catch (error) {
        this.setError('ERROR_DURING_OPERATION');
        this.$log.error(error.response.data);
      }
    },
    modifySubmitionDateAsPerNewCoverageForSUIV(submissionData) {
      const map = submissionData.map;

      // Define getTrueKey function outside the loop
      function getTrueKey(innerMap) {
        for (const [innerKey, value] of Object.entries(innerMap)) {
          if (value) {
            return innerKey;
          }
        }
        return null;
      }

      // Remove inner objects with values set to false
      for (const key of Object.keys(map)) {
        const value = map[key];
        if (typeof value === 'object') {
          for (const innerKey in value) {
            if (value.hasOwnProperty(innerKey) && value[innerKey] === false) {
              delete value[innerKey];
            }
          }
          // Remove the entire key if all values are false
          if (Object.keys(value).length === 0) {
            delete map[key];
          }
        }
      }

      // Update submissionData
      for (const [key, value] of Object.entries(map)) {
        if (typeof value === 'object') {
          const trueKey = getTrueKey(value);
          if (trueKey !== null) {
            map[key] = trueKey;
          }
        }
      }
    }
  },
  watch: {
    caseHeaderId() {
      this.loadCase();
    },
    historicalPolicycaseId() {
      this.loadCase();
    },
    selectedPolicyId(newValue) {
      let selectedPolicy = this.$refs['responseSummary_' + newValue];
      if (selectedPolicy) this.validationsOnSelectedPolicy = selectedPolicy.validationsOtherThanAuditExist();
    },
    collapsedCardsModeOn(newValue) {
      if (newValue) {
        document.body.classList.remove('overflow-hidden');
      } else {
        document.body.classList.add('overflow-hidden');
      }
    },
    responsePolicyIds() {
      this.resettingCarouselRefs = true;
      setTimeout(() => { this.resettingCarouselRefs = false }, 10);
    }
  }
}
</script>

<style lang="scss" scoped>
/* NEW BRANDING COLORS */
>>>.text-warning {
  color: #F39200 !important;
}

>>>.text-success {
  color: #00979D !important;
}

>>>.text-info {
  color: #00628D !important;
}

>>>.bg-warning {
  background: #F39200 !important;
}

>>>.bg-success {
  background: #00979D !important;
}

>>>.bg-info {
  background: #00628D !important;
}

/* PAGE LOADER */
.loader-container {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding-top: 80px;
  text-align: center;
  background: rgba(255, 255, 255, .5);
  z-index: 4;
}

/* PAGE LAYOUT */
.page-container {
  padding-top: 261px;
  /* collapsed .cards-container height */
}

/* CARDS SECTION - containers */
.cards-container {
  position: fixed;
  z-index: 3;
  top: 56px;
  /* .navbar height */
  padding-bottom: 32px !important;
  max-height: calc(100vh - 56px);
  /* minus .navbar height */
  background-color: #e8e8e8;
  box-shadow: 0 -5px 15px #616161;
  overflow: auto;
}

.col-policy-summaries {
  flex: 0 0 calc(100% - 260px);
  /* minus .col-buttons-component width  */
  max-width: calc(100% - 260px);
}

.col-buttons-component {
  /* height of the .col-label */
  flex: 0 0 260px;
  max-width: 260px;
}

.col-request-summary {
  flex: 0 0 calc(50% - 35px);
  /* calc needed to handle paddings */
  max-width: calc(50% - 35px);
}

.col-response-summaries {
  padding: 0 40px;
  flex: 0 0 calc(50% + 35px);
  /* calc needed to handle paddings */
  max-width: calc(50% + 35px);
}

@media (min-width: 1300px) {
  .col-request-summary {
    flex: 0 0 calc(33.333% - 20px);
    /* calc needed to handle paddings */
    max-width: calc(33.333% - 20px);
  }

  .col-response-summaries {
    padding: 0 40px;
    flex: 0 0 calc(66.667% + 20px);
    /* calc needed to handle paddings */
    max-width: calc(66.667% + 20px);
  }
}

@media (min-width: 1600px) {
  .col-request-summary {
    flex: 0 0 calc(25% - 12px);
    /* calc needed to handle paddings */
    max-width: calc(25% - 12px);
  }

  .col-response-summaries {
    padding: 0 40px;
    flex: 0 0 calc(75% + 12px);
    /* calc needed to handle paddings */
    max-width: calc(75% + 12px);
  }
}

.col-label {
  position: relative;
  z-index: 1;
  margin-bottom: 8px;
  font-size: .8rem;
  font-weight: 500;
  text-align: center;
  color: #616161;
}

.col-label:before {
  content: '';
  position: absolute;
  z-index: -1;
  top: 50%;
  left: 0;
  right: 0;
  border-bottom: 1px solid rgba(0, 0, 0, .2);
}

.col-label span {
  padding: 0 8px;
  background: #e8e8e8;
}

.col-response-summaries .col-label {
  margin-right: 10px;
  margin-left: 10px;
}

/* CARDS SECTION - carousel */
>>>.slick-slider {
  user-select: auto;
}

>>>.slick-list {
  margin: -32px 0 !important;
  padding: 32px 0 !important;
}

>>>.slick-track {
  display: flex !important;
}

>>>.slick-slide {
  padding: 0 10px;
  height: inherit !important;
}

>>>.slick-slide>div {
  height: 100%;
}

/* CARDS SECTION - carousel arrows */
>>>.slick-arrow {
  position: absolute;
  top: 65px;
  padding: 0;
  display: block;
  width: 24px;
  height: 48px;
  font-size: 0;
  line-height: 0;
  border: none;
  outline: none;
  background: transparent;
  transform: translateY(-50%);
  cursor: pointer;
}

>>>.slick-disabled {
  opacity: .3;
}

>>>.slick-prev {
  left: -32px;
}

>>>.slick-next {
  right: -32px;
}

/* CARDS SECTION - carousel arrow indicators */
>>>.slick-arrow:before,
>>>.slick-arrow:after {
  content: '';
  position: absolute;
  border-style: solid;
  border-color: transparent;
}

>>>.slick-arrow:before {
  top: 0;
  left: 0;
}

>>>.slick-arrow:after {
  top: 4px;
}

>>>.slick-prev:before {
  border-width: 24px 24px 24px 0;
  border-color: transparent #616161 transparent transparent;
}

>>>.slick-next:before {
  border-width: 24px 0 24px 24px;
  border-color: transparent transparent transparent #616161;
}

>>>.slick-prev:after {
  right: 0;
  border-width: 20px 20px 20px 0;
  border-color: transparent #616161 transparent transparent;
}

>>>.slick-next:after {
  left: 0;
  border-width: 20px 0 20px 20px;
  border-color: transparent transparent transparent #616161;
}

>>>.prev-highlighted .slick-prev:before {
  border-color: transparent $success transparent transparent;
}

>>>.next-highlighted .slick-next:before {
  border-color: transparent transparent transparent $success;
}

>>>.prev-highlighted .slick-prev:after {
  border-color: transparent $success transparent transparent;
}

>>>.next-highlighted .slick-next:after {
  border-color: transparent transparent transparent $success;
}

>>>.prev-border-danger .slick-prev:before {
  border-color: transparent $danger transparent transparent;
}

>>>.next-border-danger .slick-next:before {
  border-color: transparent transparent transparent $danger;
}

>>>.prev-border-warning .slick-prev:before {
  border-color: transparent $warning transparent transparent;
}

>>>.next-border-warning .slick-next:before {
  border-color: transparent transparent transparent $warning;
}

>>>.prev-border-info .slick-prev:before {
  border-color: transparent $info transparent transparent;
}

>>>.next-border-info .slick-next:before {
  border-color: transparent transparent transparent $info;
}

>>>.prev-border-light .slick-prev:before {
  border-color: transparent darken($light, 15) transparent transparent;
}

>>>.next-border-light .slick-next:before {
  border-color: transparent transparent transparent darken($light, 15);
}

/* CARDS SECTION - cards */
.cards-container .card-holder {
  height: 100%;
  outline: none;
}

.cards-container .card {
  height: 100%;
  min-height: 300px;
}

.col-request-summary .card {
  height: calc(100% - 27px);
  /* minus the height of the .col-label */
}

/* FORM */
.form-container {
  position: relative;
  z-index: 1;
}

.form-container-overlay {
  position: fixed;
  z-index: 2;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, .25);
}

.page-container>>>.anchors {
  top: 341px;
  /* .navbar height + collapsed .cards-container height + buffer */
}

/* COLLAPSED MODE */
.collapsed-mode .cards-container {
  box-shadow: 0 0 30px #fff;
  overflow: hidden;
}

.collapsed-mode .cards-container .card {
  min-height: auto;
}
</style>



// WEBPACK FOOTER //
// src/components/case/PolicyCase.vue